if (!customElements.get("cart-upsell-recommendations")) {
	customElements.define(
		"cart-upsell-recommendations",
		class CartUpsellRecommendations extends HTMLElement {
			constructor() {
				super();
				this.productId = this.dataset.productId;
				this.productIds = [
					...new Set(
						(this.dataset.productIds || this.productId || "")
							.split(",")
							.filter(Boolean)
					),
				];
				this.limit = Number(this.dataset.limit) || 4;
				this.cartProductIds = (this.dataset.cartProductIds || "")
					.split(",")
					.filter(Boolean);
				this.grid = this.querySelector(".cart-upsell__grid");
				this.closeButton = this.querySelector(".cart-upsell__close");
				this.storageKey = `cart-upsell-dismissed-${this.dataset.context || "cart"}`;
			}

			connectedCallback() {
				if (!this.productId || !this.grid) return;
				if (window.sessionStorage.getItem(this.storageKey) === "true") {
					this.remove();
					return;
				}

				this.closeButton?.addEventListener("click", () => {
					window.sessionStorage.setItem(this.storageKey, "true");
					this.remove();
				});

				Promise.all(
					this.productIds.map((productId) => {
						const url = new URL(this.dataset.url, window.location.origin);
						url.searchParams.set("product_id", productId);

						return fetch(url.toString())
							.then((response) => {
								if (!response.ok) throw new Error(response.status);
								return response.json();
							})
							.catch(() => ({ products: [] }));
					})
				)
					.then((results) => {
						const seenProductIds = new Set();
						const products = results
							.flatMap((data) => data.products || [])
							.filter((product) => !this.cartProductIds.includes(String(product.id)))
							.filter((product) => product.available)
							.filter((product) => {
								if (seenProductIds.has(product.id)) return false;
								seenProductIds.add(product.id);
								return true;
							})
							.slice(0, this.limit);

						if (!products.length) return;

						this.grid.innerHTML = products
							.map((product) => this.renderProduct(product))
							.join("");
					})
					.catch(() => {});
			}

			renderProduct(product) {
				const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
				const image = this.buildImage(product.featured_image, product.title);
				const price = product.price ? formatMoney(product.price) : "";
				const productUrl = product.url || `/products/${product.handle}`;
				const canAdd = product.available && variant?.available && product.variants?.length === 1;
				const productFormId = `cart-upsell-${this.dataset.context || "cart"}-${product.id}`;

				return `
					<div class="cart-upsell__item">
						<a class="cart-upsell__media" href="${productUrl}" aria-label="${this.escape(product.title)}">
							${image}
						</a>
						<div class="cart-upsell__content">
							<a class="cart-upsell__title" href="${productUrl}">${this.escape(product.title)}</a>
							${price ? `<div class="cart-upsell__price">${price}</div>` : ""}
							${canAdd ? this.renderAddForm(product, variant, productFormId) : this.renderOptionsLink(productUrl)}
						</div>
					</div>
				`;
			}

			renderAddForm(product, variant, productFormId) {
				return `
					<product-form class="cart-upsell__form" data-source="${this.dataset.context || "cart"}">
						<form method="post" action="${routes.cart_add_url}" id="${productFormId}" class="form" novalidate="novalidate" data-type="add-to-cart-form">
							<input type="hidden" name="id" value="${variant.id}">
							<button type="submit" name="add" class="cart-upsell__button button button--${this.dataset.buttonStyle || "tertiary"} focus-inset" aria-label="${this.escape(this.dataset.addLabel)}">
								<span>${this.escape(this.dataset.addLabel)}</span>
								<span class="sold-out-message hidden">${this.escape(window.variantStrings?.soldOut || "")}</span>
								<div class="loading-overlay__spinner hidden">
									<svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
										<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
									</svg>
								</div>
							</button>
						</form>
					</product-form>
				`;
			}

			renderOptionsLink(productUrl) {
				return `
					<a class="cart-upsell__button button button--${this.dataset.buttonStyle || "tertiary"} focus-inset" href="${productUrl}">
						<span>${this.escape(this.dataset.optionsLabel)}</span>
					</a>
				`;
			}

			buildImage(imageUrl, title) {
				if (!imageUrl) return "";

				let sizedUrl = imageUrl;
				try {
					const url = new URL(imageUrl, window.location.origin);
					url.searchParams.set("width", "360");
					sizedUrl = url.toString();
				} catch (error) {}

				return `<img src="${sizedUrl}" alt="${this.escape(title)}" loading="lazy" width="180" height="180">`;
			}

			escape(value = "") {
				return String(value)
					.replaceAll("&", "&amp;")
					.replaceAll("<", "&lt;")
					.replaceAll(">", "&gt;")
					.replaceAll('"', "&quot;")
					.replaceAll("'", "&#039;");
			}
		}
	);
}
